import React, { useCallback, useEffect, useState } from 'react';
import { ArrowDownRight, ArrowUpRight, CalendarDays, Loader2 } from 'lucide-react';
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer,
  Tooltip as ChartTooltip, XAxis, YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { adminService } from '../../../../services/adminService';
import { toast } from 'sonner';
import { SERIES_COLORS, money, money0, pct, units } from './metrics';

const WEEKDAY_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const heatColor = (value, max) => {
  if (!value || !max) return 'bg-gray-50 text-gray-400';
  const ratio = value / max;
  if (ratio > 0.8) return 'bg-amber-600 text-white';
  if (ratio > 0.6) return 'bg-amber-500 text-white';
  if (ratio > 0.4) return 'bg-amber-300 text-amber-900';
  if (ratio > 0.2) return 'bg-amber-200 text-amber-900';
  return 'bg-amber-100 text-amber-900';
};

export const SeasonalityView = ({ params }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminService.getClientPerformanceSeasonality(params));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load seasonality');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="py-16 text-center"><Loader2 className="h-8 w-8 animate-spin text-amber-500 mx-auto" /></div>;
  }
  if (!data) return null;

  const yoy = data.year_over_year || {};
  const yoyData = (yoy.months || []).map((label, idx) => {
    const row = { month: label };
    (yoy.units_sold || []).forEach((series) => {
      row[series.year] = series.values?.[idx] ?? null;
    });
    return row;
  });

  const maxHeat = Math.max(1, ...(data.heatmap || []).map((h) => h.qty_sold || 0));
  const heatByKey = {};
  (data.heatmap || []).forEach((h) => {
    heatByKey[`${h.month_label}-${h.weekday_label}`] = h;
  });
  const monthsWithData = [...new Set((data.heatmap || []).map((h) => h.month_label))];

  return (
    <div className="space-y-5" data-testid="seasonality-view">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-amber-500" /> Monthly movement
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(data.monthly || []).length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">No history in this period.</p>
          ) : (
            <div className="h-72" data-testid="seasonality-monthly-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.monthly} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit="%" domain={[0, 100]} />
                  <ChartTooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="left" dataKey="qty_sold" name="Units sold" fill="#fbbf24" radius={[3, 3, 0, 0]} />
                  <Bar yAxisId="left" dataKey="qty_returned" name="Units returned" fill="#fda4af" radius={[3, 3, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="sell_through_pct" name="Sell-through %" stroke="#059669" strokeWidth={2} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Year over year — units sold</CardTitle>
          </CardHeader>
          <CardContent>
            {!yoy.available ? (
              <p className="text-sm text-gray-500 py-8 text-center" data-testid="yoy-unavailable">
                Two years of history are needed for an overlay. Import older sales to bring this
                forward.
              </p>
            ) : (
              <div className="h-64" data-testid="yoy-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={yoyData} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <ChartTooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {(yoy.units_sold || []).map((series, i) => (
                      <Line
                        key={series.year}
                        type="monotone"
                        dataKey={series.year}
                        stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                        strokeWidth={2}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Weekday × month heatmap</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {monthsWithData.length === 0 ? (
              <p className="text-sm text-gray-500 py-8 text-center">No deliveries to map yet.</p>
            ) : (
              <table className="text-xs" data-testid="seasonality-heatmap">
                <thead>
                  <tr>
                    <th className="p-1 text-left text-gray-500">Month</th>
                    {WEEKDAY_ORDER.map((w) => (
                      <th key={w} className="p-1 text-gray-500 font-medium w-14">{w}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthsWithData.map((month) => (
                    <tr key={month}>
                      <td className="p-1 text-gray-600 font-medium">{month}</td>
                      {WEEKDAY_ORDER.map((w) => {
                        const cell = heatByKey[`${month}-${w}`];
                        return (
                          <td key={w} className="p-0.5">
                            <div
                              className={`rounded p-1.5 text-center ${heatColor(cell?.qty_sold, maxHeat)}`}
                              title={cell ? `${units(cell.qty_sold)} sold · ${pct(cell.sell_through_pct)} sell-through` : 'No deliveries'}
                            >
                              {cell?.qty_sold ? units(cell.qty_sold) : '·'}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="text-[11px] text-gray-500 mt-2">
              Units sold by delivery weekday — use it to set realistic ordering targets by season and day.
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-emerald-700 flex items-center gap-2">
              <ArrowUpRight className="h-4 w-4" /> Biggest gainers
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1" data-testid="movers-gainers">
            <p className="text-xs text-gray-500 mb-2">
              vs {data.previous_period?.start} → {data.previous_period?.end}
            </p>
            {(data.movers?.gainers || []).map((m) => (
              <div key={m.product_id} className="flex items-center justify-between text-sm">
                <span className="truncate">{m.product_name}</span>
                <span className="text-emerald-700 shrink-0">
                  +{units(m.delta)} {m.delta_pct !== null ? `(${pct(m.delta_pct)})` : ''}
                </span>
              </div>
            ))}
            {(data.movers?.gainers || []).length === 0 && (
              <p className="text-sm text-gray-500">Nothing to compare yet.</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-rose-700 flex items-center gap-2">
              <ArrowDownRight className="h-4 w-4" /> Biggest decliners
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1" data-testid="movers-decliners">
            <p className="text-xs text-gray-500 mb-2">Same window comparison</p>
            {(data.movers?.decliners || []).map((m) => (
              <div key={m.product_id} className="flex items-center justify-between text-sm">
                <span className="truncate">{m.product_name}</span>
                <span className="text-rose-700 shrink-0">
                  {units(m.delta)} {m.delta_pct !== null ? `(${pct(m.delta_pct)})` : ''}
                </span>
              </div>
            ))}
            {(data.movers?.decliners || []).length === 0 && (
              <p className="text-sm text-gray-500">Nothing to compare yet.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Month detail</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]" data-testid="seasonality-table">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="p-2 text-left">Month</th>
                  <th className="p-2 text-right">Sold</th>
                  <th className="p-2 text-right">Delivered</th>
                  <th className="p-2 text-right">Sell-through</th>
                  <th className="p-2 text-right">Return rate</th>
                  <th className="p-2 text-right">Net revenue</th>
                  <th className="p-2 text-right">Active clients</th>
                  <th className="p-2 text-right">Orders</th>
                </tr>
              </thead>
              <tbody>
                {(data.monthly || []).map((r) => (
                  <tr key={r.month} className="border-t">
                    <td className="p-2 font-medium">{r.month}</td>
                    <td className="p-2 text-right">{units(r.qty_sold)}</td>
                    <td className="p-2 text-right">{units(r.qty_delivered)}</td>
                    <td className="p-2 text-right">{pct(r.sell_through_pct)}</td>
                    <td className="p-2 text-right">{pct(r.return_rate_pct)}</td>
                    <td className="p-2 text-right">{money0(r.net_revenue)}</td>
                    <td className="p-2 text-right">{units(r.active_clients)}</td>
                    <td className="p-2 text-right">{units(r.order_count)}</td>
                  </tr>
                ))}
                {(data.monthly || []).length === 0 && (
                  <tr><td colSpan={8} className="p-8 text-center text-gray-500">No history in this period.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SeasonalityView;
